// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@werner/App/Store/Reducers";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface TrainingCalenderUpcomingDatesTableStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    dataTable?: DataTableProps;
    headerCell?: DataTableHeaderProps;
    cell?: DataTableCellProps;
    centerWrapper?: InjectableCss;
}

export const upcomingDatesTableStyles: TrainingCalenderUpcomingDatesTableStyles = {
    title: {
        variant: "h3",
        as: "h2",
        css: css`
            margin-bottom: 18px;
        `,
    },
    headerCell: {
        css: css`
            span {
                line-height: 1.5;
                font-size: 15px;
            }
        `,
    },
    cell: {
        css: css`
            span {
                line-height: 1.5;
            }
        `,
    },
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            min-width: 250px;
            min-height: 200px;
            justify-content: center;
        `,
    },
};

const styles = upcomingDatesTableStyles;

const formatDate = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
          });
};

const mapStateToProps = (state: CustomAppState) => {
    const trainingCalender = state.trainingCalender;
    return {
        upcomingDates: trainingCalender?.upcomingDates ?? [],
        isLoading: trainingCalender?.isEventDetailsLoading ?? false,
    };
};

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const TrainingCalenderUpcomingDatesTable: React.FC<Props> = ({ upcomingDates, isLoading }) => {
    if (isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }
    if (!upcomingDates.length) {
        return null;
    }

    return (
        <>
            <Typography {...styles.title}>{translate("Upcoming Dates")}</Typography>
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    <DataTableHeader {...styles.headerCell}>{translate("Date")}</DataTableHeader>
                    <DataTableHeader {...styles.headerCell}>{translate("Time")}</DataTableHeader>
                    <DataTableHeader {...styles.headerCell}>{translate("Location")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {upcomingDates.map((item, index) => (
                        <DataTableRow key={index}>
                            <DataTableCell {...styles.cell}>{formatDate(item.Date)}</DataTableCell>
                            <DataTableCell {...styles.cell}>{item.Time}</DataTableCell>
                            <DataTableCell {...styles.cell}>
                                {item.Url ? <Link href={item.Url}>{item.Location}</Link> : item.Location}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(TrainingCalenderUpcomingDatesTable),
    definition: {
        group: "Training Calender" as any,
        displayName: "Upcoming Dates",
    },
};

export default widgetModule;
